import React, { useMemo, useCallback } from "react";
import { Source, Layer, useMap } from "react-map-gl/maplibre";
import { useStore } from "@/store/store";
import { useFlightRestrictionZonesQueryV1 } from "@/api/v1/flight-restriction-zone-api";
import { matchesZoneTypeFilter, matchesAltitudeRange, apiTypeToLabel } from "@/lib/zone-type-utils";

const FLIGHT_RESTRICTIONS_SOURCE_ID = "flight-restrictions";
const FLIGHT_RESTRICTIONS_LAYER_ID = "flight-restrictions-layer";

interface FlightRestrictionsLayerProps {
  show?: boolean; // Override global store visibility
}

export const FlightRestrictionsLayer: React.FC<FlightRestrictionsLayerProps> = ({
  show,
}) => {
  const { map } = useMap();
  const storeShouldShow = useStore(
    (state) => state.shouldShowFlightRestrictions
  );
  // Use prop if provided, otherwise fall back to store
  const shouldShowFlightRestrictions = show !== undefined ? show : storeShouldShow;
  const zoneTypeFilter = useStore((state) => state.zoneTypeFilter);
  const minAltitude = useStore((state) => state.minAltitude);
  const maxAltitude = useStore((state) => state.maxAltitude);
  const setSelectedZone = useStore((state) => state.setSelectedZone);
  const setZoneDrawerOpen = useStore((state) => state.setZoneDrawerOpen);
  
  // Fetch zones from v1 API
  const { data: zones, isLoading } = useFlightRestrictionZonesQueryV1();

  // Convert zones to GeoJSON FeatureCollection
  const geoJsonData = useMemo(() => {
    if (!zones || zones.length === 0) {
      return {
        type: 'FeatureCollection' as const,
        features: []
      };
    }

    const features = zones
      .filter(zone => {
        // Apply type filter using mapping utility
        return matchesZoneTypeFilter(zone.type, zoneTypeFilter);
      })
      .filter(zone => {
        // Apply altitude range filter (check if zone overlaps with operating altitude)
        return matchesAltitudeRange(
          zone.upper_limit,
          zone.lower_limit,
          minAltitude,
          maxAltitude
        );
      })
      .filter(zone => {
        // Ensure polygon exists and is valid coordinates array
        return zone.polygon && 
               Array.isArray(zone.polygon) &&
               zone.polygon.length > 0;
      })
      .map(zone => {
        // Convert raw coordinates array to GeoJSON MultiPolygon
        const geometry: GeoJSON.MultiPolygon = {
          type: 'MultiPolygon',
          coordinates: zone.polygon
        };
        
        return {
          type: 'Feature' as const,
          id: zone.id,
          geometry,
        properties: {
          id: zone.id,
          identifier: zone.identifier,
          name: zone.name,
          type: zone.type, // API type code (D, P, R)
          type_label: apiTypeToLabel(zone.type), // UI label (Danger, Prohibited, Restricted)
          local_type: zone.local_type,
          designator: zone.designator,
          designator_icao: zone.designator_icao,
          valid_time: zone.valid_time,
          effective_begin_time: zone.effective_begin_time,
          effective_end_time: zone.effective_end_time,
          upper_limit: zone.upper_limit,
          upper_limit_uom: zone.upper_limit_uom,
          upper_limit_reference: zone.upper_limit_reference,
          lower_limit: zone.lower_limit,
          lower_limit_uom: zone.lower_limit_uom,
          lower_limit_reference: zone.lower_limit_reference,
        }
        };
      });



    return {
      type: 'FeatureCollection' as const,
      features
    };
  }, [zones, zoneTypeFilter, minAltitude, maxAltitude]);

  // Handle zone clicks
  const handleZoneClick = useCallback((event: unknown) => {
    if (!map || !zones) return;

    const e = event as { point: { x: number; y: number }; features?: unknown[] };
    // Prefer features from the event when clicking a specific layer
    const eventFeatures = Array.isArray(e.features) ? e.features : [];
    const features = eventFeatures.length > 0
      ? eventFeatures
      : map.queryRenderedFeatures([e.point.x, e.point.y], {
          layers: [`${FLIGHT_RESTRICTIONS_LAYER_ID}-fill`]
        });

    if (features && (features as unknown[]).length > 0) {
      const feature = features[0] as { id?: string; properties?: Record<string, unknown> };
      const zoneId = feature.id || feature.properties?.id as string;
      
      if (zoneId) {
        // Find the full zone data
        const zone = zones.find(z => z.id === zoneId);
        if (zone) {
          // Store the full zone object directly (type-safe)
          setSelectedZone(zone);
          setZoneDrawerOpen(true);
        }
      }
    }
  }, [map, zones, setSelectedZone, setZoneDrawerOpen]);

  // Add click event listener
  React.useEffect(() => {
    if (!map) return;

    const handleClick = (e: unknown) => {
      handleZoneClick(e);
    };

    map.on('click', `${FLIGHT_RESTRICTIONS_LAYER_ID}-fill`, handleClick);
    map.on('click', `${FLIGHT_RESTRICTIONS_LAYER_ID}-outline`, handleClick);

    // Change cursor on hover
    const handleMouseEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const handleMouseLeave = () => { map.getCanvas().style.cursor = ''; };
    map.on('mouseenter', `${FLIGHT_RESTRICTIONS_LAYER_ID}-fill`, handleMouseEnter);
    map.on('mouseleave', `${FLIGHT_RESTRICTIONS_LAYER_ID}-fill`, handleMouseLeave);

    return () => {
      map.off('click', `${FLIGHT_RESTRICTIONS_LAYER_ID}-fill`, handleClick);
      map.off('click', `${FLIGHT_RESTRICTIONS_LAYER_ID}-outline`, handleClick);
      map.off('mouseenter', `${FLIGHT_RESTRICTIONS_LAYER_ID}-fill`, handleMouseEnter);
      map.off('mouseleave', `${FLIGHT_RESTRICTIONS_LAYER_ID}-fill`, handleMouseLeave);
    };
  }, [map, handleZoneClick]);


  // Hide layer if toggled off or loading
  if (!shouldShowFlightRestrictions || isLoading) {
    return null;
  }

  // Don't hide if zones is empty - let it render empty GeoJSON
  if (!zones || zones.length === 0) {
    return null;
  }


  return (
    <Source
      id={FLIGHT_RESTRICTIONS_SOURCE_ID}
      type="geojson"
      data={geoJsonData}
    >
      {/* Fill layer - dynamic colors based on type */}
      <Layer
        id={`${FLIGHT_RESTRICTIONS_LAYER_ID}-fill`}
        type="fill"
        paint={{
          "fill-color": [
            "match",
            ["get", "type_label"],
            "Prohibited", "#ef4444", // red-500
            "Restricted", "#d946ef", // fuchsia-500
            "Danger", "#f97316",     // orange-500
            "#94a3b8" // slate-400 default
          ],
          "fill-opacity": 0.3,
        }}
      />

      {/* Outline layer - dynamic colors based on type */}
      <Layer
        id={`${FLIGHT_RESTRICTIONS_LAYER_ID}-outline`}
        type="line"
        paint={{
          "line-color": [
            "match",
            ["get", "type_label"],
            "Prohibited", "#ef4444", // red-500
            "Restricted", "#d946ef", // fuchsia-500
            "Danger", "#f97316",     // orange-500
            "#94a3b8" // slate-400 default
          ],
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            3, 0.5,
            10, 1.5,
            14, 2.5
          ],
          "line-opacity": 0.9,
        }}
      />

      {/* Labels visible at zoom 10+ */}
      <Layer
        id={`${FLIGHT_RESTRICTIONS_LAYER_ID}-labels`}
        type="symbol"
        minzoom={10}
        layout={{
          "text-field": ["get", "name"],
          "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-size": 11,
          "text-anchor": "center",
          "text-max-width": 10,
        }}
        paint={{
          "text-color": "#ffffff",
          "text-halo-color": "#000000",
          "text-halo-width": 1.5,
        }}
      />
    </Source>
  );
};